import { useState } from "react";
import { MapPin, Navigation, X } from "lucide-react";
import { useBranch } from "@/context/BranchContext";
import { haversineKm } from "@/lib/format";
import { toast } from "sonner";

export default function BranchPicker() {
  const { branches, branch, setBranch, coords, detectLocation } = useBranch();
  const [open, setOpen] = useState(false);
  const [detecting, setDetecting] = useState(false);

  if (branches.length === 0) return null;

  const detect = async () => {
    setDetecting(true);
    try {
      const r = await detectLocation();
      if (r.branch) {
        toast.success(`Cabang terdekat: ${r.branch.name}`, { description: "Gratis antar jika jarak ≤ 3 KM dari toko." });
        setOpen(false);
      }
    } catch {
      toast.error("Gagal deteksi lokasi — pilih cabang manual di bawah");
    } finally {
      setDetecting(false);
    }
  };

  return (
    <>
      <button
        data-testid="branch-picker-btn"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded-full bg-white border border-[#E5DECF] px-3 py-2 text-xs font-bold text-stone-700 hover:border-emerald-700 transition"
      >
        <MapPin className="w-4 h-4 text-emerald-700" />
        <span className="max-w-[110px] truncate">{branch ? branch.name.replace("FASMART ", "") : "Pilih Cabang"}</span>
      </button>

      {open && (
        <div data-testid="branch-picker-modal" className="fixed inset-0 z-50 bg-stone-950/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-black text-xl">Pilih Cabang FASMART</h3>
              <button data-testid="branch-picker-close" onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-stone-500 mt-1">Gratis antar maksimal 3 KM dari lokasi toko.</p>
            <button
              data-testid="branch-detect-btn"
              onClick={detect}
              disabled={detecting}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition disabled:opacity-50"
            >
              <Navigation className="w-4 h-4" /> {detecting ? "Mendeteksi…" : "Deteksi Lokasi Saya (GPS)"}
            </button>
            <div className="mt-4 space-y-2">
              {branches.map((b) => {
                const d = coords ? haversineKm(coords.lat, coords.lng, b.lat, b.lng) : null;
                const active = branch?.id === b.id;
                return (
                  <button
                    key={b.id}
                    data-testid={`branch-option-${b.id}`}
                    onClick={() => { setBranch(b); setOpen(false); }}
                    className={`w-full text-left rounded-2xl border px-4 py-3 transition ${active ? "border-emerald-700 bg-emerald-50" : "border-[#E5DECF] hover:border-emerald-600"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-sm text-stone-900">{b.name}</div>
                      {d !== null && (
                        <span className={`text-[10px] font-mono font-bold ${d <= 3 ? "text-emerald-700" : "text-rose-600"}`}>{d.toFixed(1)} km</span>
                      )}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">{b.area}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
